import {
	Opportunity,
	OpportunityView,
	allSyncErrors
} from "@/Contracts/Opportunity";
import { RecentDocumentItemView } from "@/Contracts/Documents";
import { RecentActivityItemView } from "@/Contracts/Others/RecentActivityFeed";
import {
	OpportunitiesActions,
	OpportunitiesActionTypes
} from "../../Actions/Opportunity";
import { Reducer } from "redux";
import {
	CustomViewResponse,
	CustomView,
	CustomViewsMetadata
} from "@/Contracts/UI";

export interface KanbanState {
	list: Record<string, string[]>;
	count: Record<string, number>;
	loading: Record<string, boolean>;
	data: Record<string, Opportunity>;
	estimated_value: Record<string, number>;
}
export interface OpportunityState {
	readonly isLoading: boolean;
	readonly opportunityList: Opportunity[];
	readonly filteredCountMap: Record<string, number> | null;
	readonly currentActiveOpportunity: Opportunity | null;
	readonly addOpportunityDrawerOpen: boolean;
	readonly editOpportunityDrawerOpen: boolean;
	readonly opportunityListDrawerOpen: boolean;
	readonly editKanbanOpportunity: string | null;
	readonly activeOpportunityView: OpportunityView;
	readonly activeUsers: string[];
	readonly kanban: KanbanState;
	readonly viewList: CustomViewResponse | null;
	readonly currentActiveView: CustomView | null;
	readonly viewMetadata: CustomViewsMetadata | null;
	readonly isViewChanged: boolean;
	readonly isBulkActionsRecordsSelected: boolean;
	readonly allSyncErrors: allSyncErrors | null;
	readonly recentDocumentByOpportunityId: Record<
		string,
		RecentDocumentItemView | null
	>;
	readonly recentActivityByOpportunityId: Record<
		string,
		RecentActivityItemView | null
	>;
}

export const initialOpportunityState: OpportunityState = {
	isLoading: false,
	opportunityList: [],
	filteredCountMap: null,
	currentActiveOpportunity: null,
	addOpportunityDrawerOpen: false,
	editOpportunityDrawerOpen: false,
	opportunityListDrawerOpen: false,
	activeOpportunityView: OpportunityView.Kanban,
	editKanbanOpportunity: null,
	activeUsers: [],
	kanban: {
		list: {},
		loading: {},
		count: {},
		data: {},
		estimated_value: {}
	},
	viewList: null,
	currentActiveView: null,
	viewMetadata: null,
	isViewChanged: false,
	isBulkActionsRecordsSelected: false,
	allSyncErrors: null,
	recentDocumentByOpportunityId: {},
	recentActivityByOpportunityId: {}
};

export const OpportunitiesReducer: Reducer<
	OpportunityState,
	OpportunitiesActions
> = (
	state: OpportunityState = initialOpportunityState,
	action: OpportunitiesActions
): OpportunityState => {
	switch (action.type) {
		case OpportunitiesActionTypes.SHOW_OPPORTUNITIES_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case OpportunitiesActionTypes.HIDE_OPPORTUNITIES_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case OpportunitiesActionTypes.OPPORTUNITIES_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITIES_SUCCESSFUL: {
			return {
				...state,
				opportunityList: action.opportunities
			};
		}
		case OpportunitiesActionTypes.OPPORTUNITIES_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITIES_RESET: {
			return {
				...state,
				opportunityList: new Array<Opportunity>(),
				recentDocumentByOpportunityId: {},
				recentActivityByOpportunityId: {}
			};
		}
		case OpportunitiesActionTypes.OPPORTUNITY_DETAILS_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITY_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveOpportunity: action.currentActiveOpportunity
			};
		}
		case OpportunitiesActionTypes.OPPORTUNITY_DETAILS_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITY_DETAILS_RESET: {
			return {
				...state,
				currentActiveOpportunity: null
			};
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_SUCCESSFUL: {
			return {
				...state,
				opportunityList: state.opportunityList.filter(
					opportunity =>
						opportunity.id !== action.deleteOpportunity.id
				)
			};
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_SUCCESSFUL: {
			const currOpportunityStageId =
				action.addOpportunity.opportunity_pipeline_stage_id;
			const currOpportunityId = action.addOpportunity.id;

			return {
				...state,
				opportunityList: [
					action.addOpportunity,
					...state.opportunityList
				],
				kanban: {
					...state.kanban,
					count: {
						...state.kanban.count,
						[currOpportunityStageId]:
							state.kanban.count[currOpportunityStageId] + 1
					},
					data: {
						...state.kanban.data,
						[currOpportunityId]: action.addOpportunity
					},
					list: {
						...state.kanban.list,
						[currOpportunityStageId]: [
							currOpportunityId,
							...(state.kanban.list[currOpportunityStageId] || [])
						]
					}
				}
			};
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.EDIT_OPPORTUNITY_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITY_LIST_DRAWER_OPENED: {
			return {
				...state,
				opportunityListDrawerOpen: true
			};
		}
		case OpportunitiesActionTypes.OPPORTUNITY_LIST_DRAWER_CLOSED: {
			return {
				...state,
				opportunityListDrawerOpen: false
			};
		}
		case OpportunitiesActionTypes.EDIT_OPPORTUNITY_SUCCESSFUL: {
			const newKanbanData = {
				...state.kanban,
				data: {
					...state.kanban.data,
					[action.editOpportunity.id]: action.editOpportunity
				}
			};
			const newOpportunityList = state.opportunityList.map(opp =>
				opp.id === action.editOpportunity.id
					? action.editOpportunity
					: opp
			);
			const currentActiveOpportunity =
				state.currentActiveOpportunity &&
				state.currentActiveOpportunity.id === action.editOpportunity.id
					? action.editOpportunity
					: state.currentActiveOpportunity;
			return {
				...state,
				currentActiveOpportunity,
				opportunityList: newOpportunityList,
				kanban: newKanbanData
			};
		}
		case OpportunitiesActionTypes.EDIT_OPPORTUNITY_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.OPPORTUNITY_DETAILS_UPDATED: {
			return {
				...state,
				currentActiveOpportunity: action.currentActiveOpportunity
			};
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_DRAWER_OPENED: {
			return {
				...state,
				addOpportunityDrawerOpen: true
			};
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_DRAWER_CLOSED: {
			return {
				...state,
				addOpportunityDrawerOpen: false
			};
		}
		case OpportunitiesActionTypes.EDIT_OPPORTUNITY_DRAWER_OPENED: {
			return {
				...state,
				editOpportunityDrawerOpen: true
			};
		}
		case OpportunitiesActionTypes.EDIT_OPPORTUNITY_DRAWER_CLOSED: {
			return {
				...state,
				editOpportunityDrawerOpen: false
			};
		}
		case OpportunitiesActionTypes.OPEN_EDIT_KANBAN_OPPORTUNITY_DRAWER: {
			return {
				...state,
				editKanbanOpportunity: action.editKanbanOpportunity
			};
		}
		case OpportunitiesActionTypes.CLOSE_EDIT_KANBAN_OPPORTUNITY_DRAWER: {
			return {
				...state,
				editKanbanOpportunity: null
			};
		}
		case OpportunitiesActionTypes.UPDATE_ACTIVE_OPPORTUNITY_VIEW: {
			return {
				...state,
				activeOpportunityView: action.activeView
			};
		}
		case OpportunitiesActionTypes.RESTORE_OPPORTUNITY_STATE: {
			const restoreAction = action as any;
			const oppState = restoreAction.opportunityState;

			return {
				...state,
				activeOpportunityView:
					oppState.viewMode === "list"
						? OpportunityView.List
						: OpportunityView.Kanban,
				currentActiveView:
					oppState.currentActiveView || state.currentActiveView,
				addOpportunityDrawerOpen:
					oppState.addDrawerOpen ?? state.addOpportunityDrawerOpen,
				editOpportunityDrawerOpen:
					oppState.editDrawerOpen ?? state.editOpportunityDrawerOpen
			};
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_KANBAN_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_KANBAN_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_KANBAN_SUCCESSFUL: {
			return {
				...state,
				kanban: {
					...state.kanban,
					list: {
						...state.kanban.list,
						[action.stageId]: action.list
					},
					data: {
						...state.kanban.data,
						...(action.data || {})
					}
				}
			};
		}
		case OpportunitiesActionTypes.FETCH_UPDATED_OPPORTUNITY_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_UPDATED_OPPORTUNITY_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_UPDATED_OPPORTUNITY_SUCCESSFUL: {
			return {
				...state,
				kanban: {
					...state.kanban,
					data: {
						...state.kanban.data,
						[action.opportunity.id]: action.opportunity
					}
				}
			};
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_KANBAN_SUCCESSFUL: {
			return {
				...state,
				kanban: {
					...state.kanban,
					...action.kanban
				}
			};
		}
		case OpportunitiesActionTypes.SHOW_LOADER_FOR_OPPORTUNITY_STAGE: {
			return {
				...state,
				kanban: {
					...state.kanban,
					loading: {
						...state.kanban.loading,
						[action.stageId]: true
					}
				}
			};
		}
		case OpportunitiesActionTypes.HIDE_LOADER_FOR_OPPORTUNITY_STAGE: {
			return {
				...state,
				kanban: {
					...state.kanban,
					loading: {
						...state.kanban.loading,
						[action.stageId]: false
					}
				}
			};
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_ON_KANBAN: {
			return {
				...state,
				opportunityList: state.opportunityList.map(opp =>
					opp.id === action.opportunity.id ? action.opportunity : opp
				),
				kanban: {
					...state.kanban,
					data: {
						...state.kanban.data,
						[action.opportunity.id]: action.opportunity
					}
				}
			};
		}
		case OpportunitiesActionTypes.ADD_OPPORTUNITY_ON_KANBAN: {
			return {
				...state,
				opportunityList: [action.opportunity, ...state.opportunityList],
				kanban: {
					...state.kanban,
					data: {
						...state.kanban.data,
						[action.opportunity.id]: action.opportunity
					},
					list: {
						...state.kanban.list,
						[action.opportunity.opportunity_pipeline_stage_id]: [
							action.opportunity.id,
							...state.kanban.list[
								action.opportunity.opportunity_pipeline_stage_id
							]
						]
					},
					count: {
						...state.kanban.count,
						[action.opportunity.opportunity_pipeline_stage_id]:
							state.kanban.count[
								action.opportunity.opportunity_pipeline_stage_id
							] + 1
					}
				}
			};
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_ON_KANBAN: {
			const list = [...state.kanban.list[action.opportunityStageId]];

			const newList = (list || []).filter(
				currOppId => action.opportunityId !== currOppId
			);
			return {
				...state,
				kanban: {
					...state.kanban,
					count: {
						...state.kanban.count,
						[action.opportunityStageId]:
							state.kanban.count[action.opportunityStageId] - 1
					},
					list: {
						...state.kanban.list,
						[action.opportunityStageId]: newList
					},
					estimated_value: {
						...state.kanban.estimated_value,
						[action.opportunityStageId]:
							state.kanban.estimated_value[
								action.opportunityStageId
							] - action.selectedEstimatedValue
					}
				}
			};
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_USER_FILTER: {
			return {
				...state,
				activeUsers: action.activeUsers
			};
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_FOR_KANBAN_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_FOR_KANBAN_SUCCESSFUL: {
			return {
				...state,
				kanban: {
					...state.kanban,
					...action.kanban
				}
			};
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_FOR_KANBAN_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_COUNT_FOR_KANBAN_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_COUNT_FOR_KANBAN_SUCCESSFUL: {
			return {
				...state,
				kanban: {
					...state.kanban,
					...action.kanban
				}
			};
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITIES_COUNT_FOR_KANBAN_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_DOCUMENT: {
			return {
				...state,
				currentActiveOpportunity: state.currentActiveOpportunity
					? {
							...state.currentActiveOpportunity,
							documents: {
								data: [...action.documents]
							}
					  }
					: null
			};
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_VIEW_METADATA_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_VIEW_METADATA_SUCCESSFUL: {
			return {
				...state,
				viewMetadata: action.metadata
			};
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_VIEW_METADATA_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITY_VIEWS_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITY_VIEWS_SUCCESSFUL: {
			return {
				...state,
				viewList: action.viewList
			};
		}
		case OpportunitiesActionTypes.FETCH_ALL_OPPORTUNITY_VIEWS_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_LIST_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_LIST_SUCCESSFUL: {
			return {
				...state,
				opportunityList: action.opportunityList,
				filteredCountMap: action.count
			};
		}
		case OpportunitiesActionTypes.FETCH_OPPORTUNITY_LIST_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.CREATE_OPPORTUNITY_VIEW_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.CREATE_OPPORTUNITY_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: [...(state.viewList?.data ?? []), action.view]
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case OpportunitiesActionTypes.CREATE_OPPORTUNITY_VIEW_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_VIEW_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.view,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.map(view =>
								view.id === action.view.id ? action.view : view
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_VIEW_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_VIEW_REQUESTED: {
			return state;
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_VIEW_SUCCESSFUL: {
			return {
				...state,
				currentActiveView: action.initialView,
				viewList: state.viewList
					? {
							...state.viewList,
							data: state.viewList?.data?.filter(
								view => view.id !== action.deletedView.id
							)
					  }
					: state.viewList,
				isViewChanged: false
			};
		}
		case OpportunitiesActionTypes.DELETE_OPPORTUNITY_VIEW_FAILED: {
			return state;
		}
		case OpportunitiesActionTypes.UPDATE_OPPORTUNITY_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: action.currentActiveView,
				isViewChanged: action.isViewChanged
			};
		}
		case OpportunitiesActionTypes.RESET_OPPORTUNITY_CURRENT_ACTIVE_VIEW: {
			return {
				...state,
				currentActiveView: null,
				isViewChanged: false,
				viewList: null,
				viewMetadata: null,
				recentDocumentByOpportunityId: {},
				recentActivityByOpportunityId: {}
			};
		}
		case OpportunitiesActionTypes.BULK_ACTION_RECORDS_SELECTED: {
			return {
				...state,
				isBulkActionsRecordsSelected: true
			};
		}
		case OpportunitiesActionTypes.BULK_ACTION_RECORDS_RESET: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case OpportunitiesActionTypes.BULK_DELETE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case OpportunitiesActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case OpportunitiesActionTypes.BULK_DELETE_RECORDS_FAILED: {
			return {
				...state
			};
		}
		case OpportunitiesActionTypes.BULK_UPDATE_RECORDS_REQUESTED: {
			return {
				...state
			};
		}
		case OpportunitiesActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL: {
			return {
				...state,
				isBulkActionsRecordsSelected: false
			};
		}
		case OpportunitiesActionTypes.BULK_UPDATE_RECORDS_FAILED: {
			return {
				...state
			};
		}

		case OpportunitiesActionTypes.GET_SYNC_ERRORS_SUCCESSFULL: {
			return {
				...state,
				allSyncErrors: action.errorResponse
			};
		}
		case OpportunitiesActionTypes.GET_SYNC_ERRORS_FAILED: {
			return { ...state, allSyncErrors: null };
		}

		case OpportunitiesActionTypes.MERGE_RECENT_DOCUMENT_FOR_OPPORTUNITIES: {
			return {
				...state,
				recentDocumentByOpportunityId: {
					...state.recentDocumentByOpportunityId,
					...action.map
				}
			};
		}

		case OpportunitiesActionTypes.MERGE_RECENT_ACTIVITY_FOR_OPPORTUNITIES: {
			return {
				...state,
				recentActivityByOpportunityId: {
					...state.recentActivityByOpportunityId,
					...action.map
				}
			};
		}

		default: {
			return state;
		}
	}
};

export default OpportunitiesReducer;
